/**
 * Voice Narration System
 * Handles text-to-speech for ages 3-6 with emotion support
 * Uses Web Speech API by default, with Cloud TTS as optional fallback
 */

export type VoiceEmotion = 'normal' | 'excited' | 'gentle' | 'celebrate';
export type Language = 'en' | 'es';

interface NarrationConfig {
  text: string;
  emotion: VoiceEmotion;
  language: Language;
  pauseAfter?: number; // milliseconds
  onComplete?: () => void;
}

class VoiceManager {
  private synthesis: SpeechSynthesis;
  private currentUtterance: SpeechSynthesisUtterance | null = null;
  private audioQueue: HTMLAudioElement[] = [];
  private voices: SpeechSynthesisVoice[] = [];
  private voiceEnabled: boolean = false; // Voice disabled by default

  constructor() {
    this.synthesis = window.speechSynthesis;
    this.loadVoices();
  }

  /**
   * Load available voices
   */
  private loadVoices() {
    const loadVoicesInternal = () => {
      this.voices = this.synthesis.getVoices();
      console.log(`🔊 Loaded ${this.voices.length} voices:`, this.voices.map(v => `${v.name} (${v.lang})`));
    };

    loadVoicesInternal();

    // Chrome loads voices asynchronously
    if (this.synthesis.onvoiceschanged !== undefined) {
      this.synthesis.onvoiceschanged = loadVoicesInternal;
    }
  }

  /**
   * Main narration function
   */
  async speak(config: NarrationConfig): Promise<void> {
    // If voice is disabled, skip narration but still execute callbacks
    if (!this.voiceEnabled) {
      console.log('🔊 Voice disabled - skipping narration');
      return new Promise((resolve) => {
        if (config.pauseAfter) {
          setTimeout(() => {
            config.onComplete?.();
            resolve();
          }, config.pauseAfter);
        } else {
          config.onComplete?.();
          resolve();
        }
      });
    }

    this.stop();

    // Try natural voice API first (OpenAI TTS), fall back to Web Speech on failure
    try {
      await this.speakWithCloudTTS(config);
    } catch {
      return this.speakWithWebAPI(config);
    }
  }

  /**
   * Web Speech API implementation (FREE)
   */
  private async speakWithWebAPI(config: NarrationConfig): Promise<void> {
    return new Promise((resolve) => {
      // Stop any current narration
      this.stop();

      const utterance = new SpeechSynthesisUtterance(config.text);

      const languageCode = config.language === 'en' ? 'en-US' : 'es-ES';
      const langPrefix = config.language === 'en' ? 'en' : 'es';
      const femaleHints = ['female', 'woman', 'samantha', 'victoria', 'elena', 'paloma', 'paulina'];
      const matching = this.voices.filter((v) => v.lang.toLowerCase().startsWith(langPrefix));
      const voice = matching.find((v) => femaleHints.some((h) => v.name.toLowerCase().includes(h)))
        || matching.find((v) => v.lang === languageCode)
        || matching[0];

      if (voice) utterance.voice = voice;

      console.log(`🔊 Speaking: "${config.text.substring(0, 50)}..." (${languageCode}, ${config.emotion})`);


      // Set base parameters for ages 3-6
      utterance.rate = 0.75; // 25% slower than normal
      utterance.pitch = 1.2; // Slightly higher, friendly
      utterance.volume = 1.0;
      utterance.lang = languageCode;

      // Adjust for emotion
      const emotionConfig = this.getEmotionConfig(config.emotion);
      utterance.rate = emotionConfig.rate;
      utterance.pitch = emotionConfig.pitch;

      utterance.onstart = () => {
        console.log('🔊 ✅ Speech started successfully!');
      };

      utterance.onend = () => {
        console.log('🔊 ✅ Speech completed');
        this.currentUtterance = null;

        // Handle pause after
        if (config.pauseAfter) {
          setTimeout(() => {
            config.onComplete?.();
            resolve();
          }, config.pauseAfter);
        } else {
          config.onComplete?.();
          resolve();
        }
      };

      utterance.onerror = (error) => {
        console.error('🔊 ❌ Speech error:', error.error, error);
        this.currentUtterance = null;

        // Don't try Cloud TTS fallback for now (no API endpoint)
        // Just complete gracefully
        if (config.pauseAfter) {
          setTimeout(() => {
            config.onComplete?.();
            resolve();
          }, config.pauseAfter);
        } else {
          config.onComplete?.();
          resolve();
        }
      };

      this.currentUtterance = utterance;

      // Debug: Check if synthesis is speaking
      console.log('🔊 Calling synthesis.speak()...', {
        speaking: this.synthesis.speaking,
        pending: this.synthesis.pending,
        paused: this.synthesis.paused
      });

      this.synthesis.speak(utterance);

      // Check again after calling speak
      setTimeout(() => {
        console.log('🔊 After speak() called:', {
          speaking: this.synthesis.speaking,
          pending: this.synthesis.pending,
          paused: this.synthesis.paused
        });
      }, 100);
    });
  }

  /**
   * OpenAI TTS implementation (retries on 429 rate limit)
   */
  private async fetchTTSWithRetry(url: string, opts: RequestInit, retries = 0): Promise<Response> {
    const res = await fetch(url, opts);
    if (res.status === 429 && retries < 2) {
      await new Promise((r) => setTimeout(r, 2000 * (retries + 1)));
      return this.fetchTTSWithRetry(url, opts, retries + 1);
    }
    return res;
  }

  private async speakWithCloudTTS(config: NarrationConfig): Promise<void> {
    const response = await this.fetchTTSWithRetry(
      '/api/text-to-speech',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: config.text,
          language: config.language,
        }),
      }
    );

    if (!response.ok) {
      throw new Error(`TTS API failed: ${response.status}`);
    }

    const audioBlob = await response.blob();
    const audioUrl = URL.createObjectURL(audioBlob);

    return new Promise((resolve, reject) => {
      const audio = new Audio(audioUrl);

      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
        if (config.pauseAfter) {
          setTimeout(() => {
            config.onComplete?.();
            resolve();
          }, config.pauseAfter);
        } else {
          config.onComplete?.();
          resolve();
        }
      };

      audio.onerror = (error) => {
        console.error('Audio playback error:', error);
        URL.revokeObjectURL(audioUrl);
        reject(error);
      };

      this.audioQueue.push(audio);
      audio.play();
    });
  }

  /**
   * Get emotion-specific voice parameters
   */
  private getEmotionConfig(emotion: VoiceEmotion): { rate: number; pitch: number } {
    const configs = {
      normal: { rate: 0.75, pitch: 1.2 },
      excited: { rate: 0.85, pitch: 1.4 },
      gentle: { rate: 0.65, pitch: 1.1 },
      celebrate: { rate: 0.9, pitch: 1.5 },
    };
    return configs[emotion];
  }

  /**
   * Stop current narration
   */
  stop(): void {
    if (this.currentUtterance) {
      this.synthesis.cancel();
      this.currentUtterance = null;
    }

    this.audioQueue.forEach(audio => {
      audio.pause();
      audio.currentTime = 0;
    });
    this.audioQueue = [];
  }

  /**
   * Pause current narration
   */
  pause(): void {
    if (this.currentUtterance && this.synthesis.speaking) {
      this.synthesis.pause();
    }

    this.audioQueue.forEach(audio => audio.pause());
  }

  /**
   * Resume paused narration
   */
  resume(): void {
    if (this.currentUtterance && this.synthesis.paused) {
      this.synthesis.resume();
    }

    this.audioQueue.forEach(audio => audio.play());
  }

  /**
   * Check if currently speaking
   */
  isSpeaking(): boolean {
    if (!this.voiceEnabled) {
      return false;
    }
    return this.synthesis.speaking || this.audioQueue.length > 0;
  }

  /**
   * Enable voice narration
   */
  enableVoice(): void {
    this.voiceEnabled = true;
    console.log('🔊 Voice narration enabled');
  }

  /**
   * Disable voice narration
   */
  disableVoice(): void {
    this.voiceEnabled = false;
    this.stop(); // Stop any current narration
    console.log('🔊 Voice narration disabled');
  }

  /**
   * Check if voice is enabled
   */
  isVoiceEnabled(): boolean {
    return this.voiceEnabled;
  }
}

// Export singleton instance
export const voiceManager = new VoiceManager();
